import {
  Button,
  CloseButton,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import CalendarIcon from '@/ui/assets/CalendarIcon'
import FilterIcon from '@/ui/assets/FilterIcon'
import ShearchLoupeIcon from '@/ui/assets/ShearchLoupeIcon'

import { useMedicalRecordManagementContext } from '../contexts/MedicalRecordManagementContext'

export default function MedicalRecordManagementFilter() {
  const { t } = useTranslation()

  const {
    isOpen: isFilterMenuOpen,
    onToggle: onFilterMenuToggle,
    onClose: onFilterMenuClose
  } = useDisclosure()

  const { filter, setMonth, setPersonaName } =
    useMedicalRecordManagementContext()

  const isFilterActive = Boolean(filter.month || filter.personaName)
  const bg = isFilterActive ? 'custom.blue.darker' : undefined
  const color = isFilterActive ? '#FFFFFF' : undefined

  const filterDisplay = [
    filter.month && (
      <Flex align={'center'} gap={1}>
        <CalendarIcon boxSize={'18px'} />
        <Text>
          {moment(filter.month)
            .format('MMMM YYYY')
            .replace(/\w/, (r) => r.toUpperCase())}
        </Text>
      </Flex>
    ),
    filter.personaName && (
      <Flex align={'baseline'} gap={1}>
        <ShearchLoupeIcon boxSize={'18px'} />
        <Text>{filter.personaName}</Text>
      </Flex>
    )
  ]
    .filter(Boolean)
    .flatMap((item, i, { length }) => (i < length ? item : [item, ', ']))

  return (
    <Flex
      wrap={'wrap'}
      justify={'space-between'}
      align={'center'}
      w={'full'}
      borderWidth={'1px'}
      p={2}
      borderRadius={'4px'}
    >
      <Flex
        flex={1}
        color={'custom.gray.200'}
        fontSize={'16'}
        fontWeight={500}
        gap={2}
      >
        {filterDisplay.length > 0 ? (
          filterDisplay
        ) : (
          <Text pl={'4'}>{t('medicalRecordManagement.filter.empty')}</Text>
        )}
      </Flex>
      <Menu isOpen={isFilterMenuOpen} onClose={onFilterMenuClose}>
        <MenuButton
          as={Button}
          variant="outline"
          onClick={onFilterMenuToggle}
          rightIcon={<FilterIcon color={color} />}
          borderWidth="1px"
          borderRadius="md"
          py="2"
          px="3"
          w="130px"
          {...(bg && { bg })}
          _hover={{ textDecoration: 'none' }}
          _active={{ textDecoration: 'none' }}
        >
          <Text w="fit-content" fontWeight="500" color={color}>
            {t('medicalRecordManagement.filter.trigger')}
          </Text>
        </MenuButton>

        <MenuList
          w={'390px'}
          px="4"
          pb="4"
          display="flex"
          flexDir="column"
          gap="4"
          zIndex={2}
        >
          <Flex
            justifyContent={'space-between'}
            w={'full'}
            alignItems={'center'}
            h={'fit-content'}
            mb={'-2'}
          >
            <Text fontWeight={'500'} color={'custom.gray.100'}>
              {t('medicalRecordManagement.filter.title')}
            </Text>
            <Flex gap={2} alignItems="center">
              <CloseButton onClick={onFilterMenuClose} />
            </Flex>
          </Flex>
          <Divider />
          <Flex direction={'column'} gap={2}>
            <Flex justifyContent={'space-between'} w={'full'}>
              <Text fontWeight={'500'} color={'custom.gray.300'}>
                {t('medicalRecordManagement.filter.options.persona.label')}
              </Text>
              <Button
                variant={'link'}
                colorScheme={'blue'}
                color={'custom.blue.darker'}
                onClick={() => setPersonaName(undefined)}
              >
                {t('medicalRecordManagement.filter.options.persona.clear')}
              </Button>
            </Flex>
            <InputGroup>
              <InputLeftElement>
                <ShearchLoupeIcon boxSize={'18px'} />
              </InputLeftElement>
              <Input
                value={filter.personaName}
                onChange={(e) =>
                  setPersonaName(e.currentTarget.value || undefined)
                }
                borderColor={filter.personaName ? 'custom.gray.400' : undefined}
                placeholder={t(
                  'medicalRecordManagement.filter.options.persona.placeholder'
                )}
              />
            </InputGroup>
          </Flex>
          <Divider />
          <Flex direction={'column'} gap={2}>
            <Flex justifyContent={'space-between'} w={'full'}>
              <Text
                color={'custom.gray.300'}
                fontSize={'16px'}
                fontWeight={500}
              >
                {t('medicalRecordManagement.filter.options.month.label')}
              </Text>
              <Button
                variant={'link'}
                colorScheme={'blue'}
                color={'custom.blue.darker'}
                onClick={() => setMonth('')}
              >
                {t('medicalRecordManagement.filter.options.month.clear')}
              </Button>
            </Flex>
            <Input
              type={'month'}
              value={filter.month}
              onChange={(e) => setMonth(e.currentTarget.value || '')}
              max={moment().format(moment.HTML5_FMT.MONTH)}
              borderColor={filter.month ? 'custom.gray.400' : undefined}
            />
          </Flex>
        </MenuList>
      </Menu>
    </Flex>
  )
}
